import { useRef, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Check, Circle, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { buttonVariants } from '@/components/ui/button-variants';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { useAppDispatch } from '@/Redux/store';
import { login } from '@/Redux/Slices/authSlice';
import { useAuth } from '@/Hooks/useAuth';
import { AuthShell } from './AuthShell';
import { AuthField } from './AuthField';
import {
  FIELD_ORDER,
  PASSWORD_MIN_LENGTH,
  browserTimezone,
  describeRegistrationFailure,
  passwordByteLength,
  passwordClasses,
  slugError,
  slugify,
  toRegisterBody,
  validateRegistration,
  type RegistrationErrors,
  type RegistrationForm,
} from './registration';

/** What `POST /tenants/register` answers with. No tokens — see `signUp` below. */
interface RegisteredTenant {
  tenant: { id: string; name: string; slug: string };
  owner: { id: string; email: string | null };
  branches: number;
}

const EMPTY: RegistrationForm = {
  name: '',
  slug: '',
  firstName: '',
  lastName: '',
  email: '',
  password: '',
};

/**
 * Self-serve signup — the public `/register` route.
 *
 * ── WHERE IT SITS ─────────────────────────────────────────────────────────────
 * Registered in `Routes.tsx` as a SIBLING of `/login`, outside the
 * `<Route element={<PermissionRoute />}>` wrapper. That placement is the whole
 * bug fix: `PermissionRoute` sends every unauthenticated request to `/login`,
 * and because `/register` had no route of its own it fell through to the `*`
 * catch-all *inside* that wrapper — so a stranger clicking "Register" on the
 * marketing site was bounced to a login form for the account they were trying
 * to create.
 *
 * ── HOW THE PERSON ENDS UP SIGNED IN ──────────────────────────────────────────
 * The endpoint returns NO tokens. It answers with the tenant, the owner and
 * `nextStep: "POST /api/auth/login ... then create a branch"`, which is the
 * server telling us plainly that authentication is a second call. Two options
 * were open: land on `/login` with the email prefilled, or log in here with the
 * credentials the user has just typed. This does the second, because the first
 * asks someone to type the password they chose fifteen seconds ago into a fresh
 * form — and a signup that ends at a login form reads as a signup that failed.
 *
 * The risk of the two-step is a gap: the tenant is created and then something
 * goes wrong before the session exists, leaving a person who believes they have
 * no account when in fact they do — and who will burn another of their five
 * hourly attempts trying again with a slug that is now taken. So the "created"
 * state is committed to the UI BEFORE the login call is made, not after it
 * returns. From the moment the 201 lands, this screen says the venue exists,
 * whatever happens next; a failed login downgrades it to "created, please log
 * in" with a link that carries the email across. There is no ordering of
 * failures that produces "registration failed" for a venue that was created.
 *
 * ── HOW IT TALKS TO THE API ───────────────────────────────────────────────────
 * The axios client from `@/lib/api`, not RTK Query — the same choice `login`
 * and `useSetupStatus` make. RTK Query is this app's CACHE, and a one-shot
 * unauthenticated write has nothing to cache and no tag to invalidate; going
 * through `baseApi` would also register a slice into the store for a screen
 * that is unmounted the moment it succeeds.
 */
export default function Register() {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const { isAuthenticated } = useAuth();

  const [form, setForm] = useState<RegistrationForm>(EMPTY);
  const [errors, setErrors] = useState<RegistrationErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [status, setStatus] = useState<'form' | 'creating' | 'created'>('form');
  const [created, setCreated] = useState<{ venue: string; email: string } | null>(null);
  const [signInError, setSignInError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  /** True once the slug has been typed in by hand — after that we stop guessing. */
  const slugTouched = useRef(false);
  const inputs = useRef<Partial<Record<keyof RegistrationForm, HTMLInputElement | null>>>({});

  // Auto-login landed. Overview renders `SetupCard`, which is the "create your
  // first branch" nudge this tenant needs — `branches: 0` on a fresh tenant.
  if (isAuthenticated) return <Navigate to="/" replace />;

  const set = (field: keyof RegistrationForm, value: string) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      // The slug is derived from the venue name until someone edits it. Deriving
      // it forever would fight the user; never deriving it makes them invent a
      // DNS label from nothing, which is the field this form would lose people on.
      if (field === 'name' && !slugTouched.current) next.slug = slugify(value);
      return next;
    });
    // Clear the refusal on the field being fixed, but not the others — re-running
    // the whole validation on every keystroke would shout about the password
    // while someone is still typing their venue name.
    setErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev));
    if (field === 'slug') slugTouched.current = true;
  };

  const fieldError = (field: keyof RegistrationForm) =>
    errors[field] ? t(`auth.register.errors.${errors[field]}`) : null;

  const signUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    const found = validateRegistration(form);
    if (Object.keys(found).length) {
      setErrors(found);
      // Focus the first refused field rather than only colouring it: on a phone
      // the offending input is often scrolled off-screen, and a form that
      // silently does nothing on submit is indistinguishable from a broken one.
      const first = FIELD_ORDER.find((field) => found[field]);
      if (first) inputs.current[first]?.focus();
      return;
    }

    setStatus('creating');
    let result: RegisteredTenant;
    try {
      const response = await api.post<RegisteredTenant>(
        '/tenants/register',
        toRegisterBody(form, { locale: i18n.language, timezone: browserTimezone() }),
      );
      result = response.data;
    } catch (error) {
      const failure = describeRegistrationFailure(error);
      const message = failure.message ?? t(`auth.register.errors.${failure.key}`);
      setFormError(message);
      // A 409 belongs to one input. Shown at the top of the form (so it is
      // announced once, as an alert) and focused (so the cursor is already in
      // the field that has to change).
      if (failure.field) inputs.current[failure.field]?.focus();
      setStatus('form');
      return;
    }

    // COMMITTED FIRST. Everything below this line can fail without the person
    // being told their venue was not created, because by now it was.
    const email = result.owner.email ?? form.email.trim().toLowerCase();
    setCreated({ venue: result.tenant.name, email });
    setStatus('created');

    const signedIn = await dispatch(login({ identifier: email, password: form.password }));
    if (login.rejected.match(signedIn)) {
      setSignInError(String(signedIn.payload ?? ''));
      return;
    }
    toast.success(t('auth.register.nextStep'));
  };

  if (status === 'created' && created) {
    return (
      <AuthShell>
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">
              {t('auth.register.created.title', {
                venue: created.venue,
              })}
            </CardTitle>
            <CardDescription>
              {signInError
                ? t('auth.register.created.signInFailed')
                : t('auth.register.created.body')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="break-words text-sm font-medium">{created.email}</p>
            {signInError ? (
              <>
                <p className="text-xs text-ink-muted">{signInError}</p>
                <Link
                  to="/login"
                  state={{ email: created.email }}
                  className={buttonVariants({ className: 'w-full' })}
                >
                  {t('auth.register.created.goToLogin')}
                </Link>
              </>
            ) : null}
          </CardContent>
        </Card>
      </AuthShell>
    );
  }

  const busy = status === 'creating';
  const slugPreview = slugError(form.slug) ? null : form.slug;

  return (
    <AuthShell width="wide">
      <Card>
        <CardHeader>
          <CardTitle className="text-xl">{t('auth.register.title')}</CardTitle>
          <CardDescription>
            {t('auth.register.subtitle')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={signUp} noValidate className="space-y-5">
            {formError ? (
              // `role="alert"` so the refusal is announced. Without it a screen
              // reader user gets a form that appears to have done nothing.
              <p
                role="alert"
                className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/50 dark:text-red-300"
              >
                {formError}
              </p>
            ) : null}

            <fieldset className="space-y-4">
              <legend className="pb-1 text-xs font-semibold uppercase text-ink-muted">
                {t('auth.register.venueHeading')}
              </legend>

              <AuthField
                ref={(el) => {
                  inputs.current.name = el;
                }}
                label={t('auth.register.venueName')}
                hint={t('auth.register.venueNameHint')}
                error={fieldError('name')}
                value={form.name}
                onChange={(e) => set('name', e.target.value)}
                autoComplete="organization"
                maxLength={120}
                autoFocus
              />

              <AuthField
                ref={(el) => {
                  inputs.current.slug = el;
                }}
                label={t('auth.register.slug')}
                hint={t('auth.register.slugHint')}
                error={fieldError('slug')}
                value={form.slug}
                onChange={(e) => set('slug', e.target.value)}
                // A DNS label is always Latin and always left-to-right, even when
                // the rest of the page is Arabic — `dir="ltr"` stops the dashes
                // and digits from being reordered around it on screen.
                dir="ltr"
                inputMode="url"
                autoComplete="off"
                autoCapitalize="none"
                spellCheck={false}
                maxLength={40}
              />
              {slugPreview ? (
                <p dir="ltr" className="-mt-2 truncate font-mono text-xs text-ink-muted">
                  {t('auth.register.slugPreview', {
                    slug: slugPreview,
                  })}
                </p>
              ) : null}
            </fieldset>

            <fieldset className="space-y-4">
              <legend className="pb-1 text-xs font-semibold uppercase text-ink-muted">
                {t('auth.register.ownerHeading')}
              </legend>

              <div className="grid gap-4 sm:grid-cols-2">
                <AuthField
                  ref={(el) => {
                    inputs.current.firstName = el;
                  }}
                  label={t('auth.register.firstName')}
                  error={fieldError('firstName')}
                  value={form.firstName}
                  onChange={(e) => set('firstName', e.target.value)}
                  autoComplete="given-name"
                  maxLength={80}
                />
                <AuthField
                  ref={(el) => {
                    inputs.current.lastName = el;
                  }}
                  label={t('auth.register.lastName')}
                  optionalLabel={t('auth.register.optional')}
                  value={form.lastName}
                  onChange={(e) => set('lastName', e.target.value)}
                  autoComplete="family-name"
                  maxLength={80}
                />
              </div>

              <AuthField
                ref={(el) => {
                  inputs.current.email = el;
                }}
                type="email"
                label={t('auth.register.email')}
                hint={t('auth.register.emailHint')}
                error={fieldError('email')}
                value={form.email}
                onChange={(e) => set('email', e.target.value)}
                dir="ltr"
                inputMode="email"
                autoComplete="email"
                autoCapitalize="none"
                spellCheck={false}
                maxLength={254}
              />

              <AuthField
                ref={(el) => {
                  inputs.current.password = el;
                }}
                type={showPassword ? 'text' : 'password'}
                label={t('auth.register.password')}
                error={fieldError('password')}
                value={form.password}
                onChange={(e) => set('password', e.target.value)}
                autoComplete="new-password"
                adornment={
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-pressed={showPassword}
                    aria-label={
                      showPassword
                        ? t('auth.register.hidePassword')
                        : t('auth.register.showPassword')
                    }
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" aria-hidden />
                    ) : (
                      <Eye className="h-4 w-4" aria-hidden />
                    )}
                  </Button>
                }
              />

              {/*
                The policy, shown while it is being met rather than after it is
                broken. This route allows five attempts an HOUR per IP, so a
                password refused by the server is not a small cost — and the
                three rules are not guessable from "password is not strong
                enough". `aria-live` is deliberately absent: these tick as you
                type and would otherwise be read aloud on every keystroke.
              */}
              <ul className="space-y-1 text-xs text-ink-muted">
                <li className="font-medium">
                  {t('auth.register.rules.heading')}
                </li>
                {/* Every rule reads as unmet on an EMPTY field. "At most 72
                    bytes" is technically satisfied by the empty string, and a
                    green tick on a password nobody has typed says the form has
                    approved of something — which is the one thing this list must
                    never do. */}
                <Rule met={form.password.length >= PASSWORD_MIN_LENGTH}>
                  {t('auth.register.rules.length')}
                </Rule>
                <Rule met={passwordClasses(form.password) >= 3}>
                  {t('auth.register.rules.classes')}
                </Rule>
                <Rule
                  met={form.password.length > 0 && passwordByteLength(form.password) <= 72}
                >
                  {t('auth.register.rules.bytes')}
                </Rule>
              </ul>
            </fieldset>

            <Button type="submit" className="w-full" disabled={busy}>
              {busy
                ? t('auth.register.submitting')
                : t('auth.register.submit')}
            </Button>

            <p className="text-center text-sm text-ink-muted">
              {t('auth.haveAccount')}{' '}
              <Link to="/login" className="font-medium text-ink underline underline-offset-4">
                {t('auth.login')}
              </Link>
            </p>
          </form>
        </CardContent>
      </Card>
    </AuthShell>
  );
}

function Rule({ met, children }: { met: boolean; children: React.ReactNode }) {
  return (
    <li className="flex items-center gap-2">
      {met ? (
        <Check className="h-3.5 w-3.5 shrink-0 text-emerald-600 dark:text-emerald-400" aria-hidden />
      ) : (
        <Circle className="h-3.5 w-3.5 shrink-0 opacity-40" aria-hidden />
      )}
      <span className={met ? 'text-emerald-700 dark:text-emerald-400' : undefined}>{children}</span>
    </li>
  );
}
